import React, { useEffect, useState, useRef } from "react";
import {
  cleaningEffectivness,
  cleaningEffectivnessStatus,
} from "../../network/cipAPI";
import AgTable from "../../components/AgTable";
import Charts from "../../FileUpload/chart";
import {
  dateTimeFormatter,
  getSortedData,
  averageFormatter,
  averageEffectivenessFormatter,
  predictTypeFormatter,
  cleaningFormatter,
  cleaningEfficitivenessFormatter,
} from "../../utils/tableDataFormatter";
import PageLoading from "../../components/PageLoading";
import { useDispatch, useSelector } from "react-redux";
import {
  storeClenaingEffectiness,
  storeClenaingEffectinessThreshold,
} from "../../Store/TemplateStore/template.action";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import { useTranslation } from "react-i18next";
import { extractPlantId, getIndex } from "../../utils/Utils";
import MetricSummaryCard from "../../components/MetricSummaryCard";
import { getCleaningMetricsCardsConfig } from "../cleaningMetricsCards.config";
import "./index.scss";

const HistoricalCIPEffectivness = ({ activeStep, segmentedCips }) => {
  const { t } = useTranslation();
  const plantId = extractPlantId();
  const dispatch = useDispatch();
  const intervalRef = useRef(null);

  const {
    cleaningEffectiness,
    cleanData: cleaningResponse,
    cleanDate: date,
    pressure,
    metricData,
  } = useSelector((state) => state);

  const [rowData, setRowData] = useState(cleaningEffectiness);
  const [isLoading, setIsLoading] = useState(false);
  const [processedPercentage, setprocessedPercentage] = useState(false);
  const [threshold, setThreshold] = useState(null);

  const colDefs = [
    {
      headerName: t("HistoricalCIPEffectivnessTableStrings.index"),
      valueGetter: (params) =>
        getIndex(params, params.data.cleaning_start_time),

      flex: 0.75,
      minWidth: 50,
      headerClass: "custom-Header-cip-user-table",
    },
    {
      headerName: t("HistoricalCIPEffectivnessTableStrings.start_date"),
      field: "cleaning_start_time",
      editable: false,
      sortable: true,
      flex: 1,
      valueFormatter: dateTimeFormatter,
      minWidth: 200,
      headerClass: "custom-Header-cip-user-table",
    },
    {
      headerName: t("HistoricalCIPEffectivnessTableStrings.end_date"),
      field: "cleaning_end_time",
      editable: false,
      sortable: true,
      flex: 1,
      valueFormatter: dateTimeFormatter,
      minWidth: 200,
      headerClass: "custom-Header-cip-user-table",
    },
    {
      headerName: t("HistoricCipTableStrings.next_cec_start_date"),
      field: "next_cec_start_date",
      editable: false,
      flex: 1,
      valueFormatter: dateTimeFormatter,
      sortable: true,
      minWidth: 200,
      headerClass: "custom-Header-cip-user-table",
    },
    {
      headerName: t("HistoricalCIPEffectivnessTableStrings.type"),
      field: "cleaning_type",
      editable: false,
      sortable: true,
      flex: 1,
      valueFormatter: (params) => predictTypeFormatter(params, t),
      minWidth: 100,
      headerClass: "custom-Header-cip-user-table",
    },
    {
      headerName: t("HistoricalCIPEffectivnessTableStrings.effectiveness"),
      field: "effectiveness",
      editable: false,
      sortable: true,
      flex: 1,
      valueFormatter: (params) => cleaningEfficitivenessFormatter(params),
      minWidth: 180,
      headerClass: "custom-Header-cip-user-table",
    },
    {
      headerName: t("HistoricalCIPEffectivnessTableStrings.efficiency"),
      field: "slope_new",
      editable: false,
      sortable: true,
      flex: 1,
      valueFormatter: (params) => cleaningFormatter(params),
      minWidth: 250,
      headerClass: "custom-Header-cip-user-table",
    },
    {
      headerName: t(
        "HistoricalCIPEffectivnessTableStrings.avg_bw_cleaning_effectiveness"
      ),
      field: "avg_backwash_effectiveness",
      editable: false,
      sortable: true,
      flex: 1,
      valueFormatter: (params) =>
        averageEffectivenessFormatter(
          params,
          "avg_backwash_effectiveness",
          "sd_backwash_effectiveness"
        ),
      minWidth: 225,
      headerClass: "custom-Header-cip-user-table",
    },
    {
      headerName: t(
        "HistoricalCIPEffectivnessTableStrings.avg_bw_cleaning_efficiency"
      ),
      field: "avg_backwash_slope",
      editable: false,
      sortable: true,
      flex: 1,
      valueFormatter: (params) =>
        averageFormatter(params, "avg_backwash_slope", "sd_backwash_slope"),
      minWidth: 275,
      headerClass: "custom-Header-cip-user-table",
    },
    {
      headerName: t(
        "HistoricalCIPEffectivnessTableStrings.avg_ceb_cleaning_effectiveness"
      ),
      field: "avg_ceb_effectiveness",
      editable: false,
      sortable: true,
      flex: 1,
      valueFormatter: (params) =>
        averageEffectivenessFormatter(
          params,
          "avg_ceb_effectiveness",
          "sd_ceb_effectiveness"
        ),
      minWidth: 225,
      headerClass: "custom-Header-cip-user-table",
    },
    {
      headerName: t(
        "HistoricalCIPEffectivnessTableStrings.avg_ceb_cleaning_efficiency"
      ),
      field: "avg_ceb_slope",
      editable: false,
      sortable: true,
      flex: 1,
      valueFormatter: (params) =>
        averageFormatter(params, "avg_ceb_slope", "sd_ceb_slope"),
      minWidth: 250,
      headerClass: "custom-Header-cip-user-table",
    },
  ];
  const completed_state = (responseData) => {
    const data = JSON.parse(responseData.result);
    const keys = Object.keys(data["cleaning_start_time"]);
    const result = keys.map((key, index) => ({
      cleaning_start_time: data["cleaning_start_time"][key],
      cleaning_end_time: data["cleaning_end_time"][key],
      cleaning_type: data["cleaning_type"][key],
      effectiveness: data["effectiveness"][key],
      slope_new: data["slope_new"][key],
      avg_backwash_effectiveness: data["avg_backwash_effectiveness"][key],
      sd_backwash_effectiveness: data["sd_backwash_effectiveness"][key],

      avg_backwash_slope: data["avg_backwash_slope"][key],
      sd_backwash_slope: data["sd_backwash_slope"][key],

      avg_ceb_effectiveness: data["avg_ceb_effectiveness"][key],
      sd_ceb_effectiveness: data["sd_ceb_effectiveness"][key],

      avg_ceb_slope: data["avg_ceb_slope"][key],
      sd_ceb_slope: data["sd_ceb_slope"][key],
    }));

    const sortedData = getSortedData(result);

    // Check if sortedData exists and has elements
    if (sortedData && sortedData.length > 0) {
      // Update each object with next_cec_start_date as the next cleaning_start_time
      sortedData.forEach((item, index) => {
        const sortedDataLength = sortedData.length;
        item.next_cec_start_date =
          index < sortedDataLength - 1
            ? sortedData[index + 1].cleaning_start_time
            : null;
      });
    }
    dispatch(storeClenaingEffectiness(sortedData));
    setRowData(sortedData);
    // fetch threshold data from result
    if (data?.threshold) {
      setThreshold(data?.threshold);
      dispatch(storeClenaingEffectinessThreshold(data?.threshold));
    }
    setIsLoading(false);
    clearInterval(intervalRef.current);
  };

  const checkStatus = async (task_id) => {
    try {
      const effectivenessStatus = await cleaningEffectivnessStatus(task_id);
      try {
        if (effectivenessStatus?.data?.status === "completed") {
          completed_state(effectivenessStatus?.data);
        } else if (effectivenessStatus?.data?.status === "pending") {
          setIsLoading(false);
          clearInterval(intervalRef.current);
          setprocessedPercentage("");
        } else if (effectivenessStatus?.data?.status === "running") {
          setIsLoading(true);
          setprocessedPercentage(effectivenessStatus?.data.processed);
        } else if (effectivenessStatus?.data?.status === "failed") {
          setIsLoading(false);
          // setprocessedPercentage("");
          clearInterval(intervalRef.current);
          createNotification(
            ERROR_NOTIFICATION,
            t("notificationStrings.fetchFail"),
            ""
          );
        }
      } catch (error) {
        clearInterval(intervalRef.current);
        setRowData([]);
        setIsLoading(false);
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.fetchFail"),
          ""
        );
      }
    } catch (err) {
      if (err.code === "ERR_NETWORK" || err.response.status === 502) {
        console.log("Error is " + err);
      } else {
        clearInterval(intervalRef.current);
        setRowData([]);
        setIsLoading(false);
        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.fetchFail"),
          ""
        );
      }
    }
  };

  useEffect(() => {
    if (plantId) {
      const fetchData = async () => {
        try {
          const res = await cleaningEffectivness();
          if (res && res.data?.status == "completed") {
            completed_state(res?.data);
          } else {
            intervalRef.current = setInterval(function () {
              checkStatus(res.data?.task_id);
            }, 5000);
            setprocessedPercentage("1%");
            setIsLoading(true);
          }
        } catch (err) {
          setRowData([]);
          setIsLoading(false);
          createNotification(
            ERROR_NOTIFICATION,
            t("notificationStrings.fetchFail"),
            ""
          );
        }
      };
      fetchData();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const hasValuesGreaterThan100 = rowData?.some(
    (data) =>
      data.effectiveness > 100 ||
      data.avg_ceb_effectiveness > 100 ||
      data.avg_backwash_effectiveness > 100
  );

  const hasValuesLessThan0 = rowData?.some(
    (data) =>
      data.effectiveness < 0 ||
      data.avg_ceb_effectiveness < 0 ||
      data.avg_backwash_effectiveness < 0
  );

  const metricCardsConfig = metricData
    ? getCleaningMetricsCardsConfig(metricData, t)
    : [];

  return (
    <div>
      {isLoading || !rowData ? (
        <PageLoading
          customHeight={"90px"}
          iconHeight={"height--70"}
          message={t("ValueStrings.loaderMessage")}
          percentage={processedPercentage}
        ></PageLoading>
      ) : (
        <>
          <div className="descriptionText marginTop10 marginBtm10">
            {t("cleaningEffectivnessStrings.cleaningDescription")}
          </div>

          {/* Display operational time and water recovery above table */}
          <div className="operational-metrics">
            {metricCardsConfig.map((card) => (
              <MetricSummaryCard
                key={card.key}
                title={card.title}
                rawValue={card.rawValue}
                value={card.value}
                targetLabel={card.targetLabel}
                targetValue={card.targetValue}
                rangeLabel={card.rangeLabel}
                rangeValue={card.rangeValue}
                infoDescription={card.infoDescription}
                icon={card.icon}
              />
            ))}
          </div>

          <AgTable
            columnDefs={colDefs}
            rowData={rowData}
            name={`${t("excelSheetNames.cleaningEffectivness")}.csv`}
            infoPopoverText={t(
              "IdentifiedCIPStrings.cleaningEffectivenessSummaryLine"
            )}
            questionPopoverText={t(
              "IdentifiedCIPStrings.troubleshootingCIPsAndCEBs"
            ).replaceAll("<unit>", pressure || "bar/psig")}
            dynamicTextConditions={{
              hasValuesGreaterThan100,
              hasValuesLessThan0,
            }}
          />
          <div>
            {hasValuesGreaterThan100 && (
              <div className="descriptionText marginTop10 marginBtm10">
                {t("cleaningEffectivnessStrings.noteForValuesrGeaterThan100")}
              </div>
            )}
            {hasValuesLessThan0 && (
              <div className="descriptionText marginTop10 marginBtm10">
                {t("cleaningEffectivnessStrings.noteForValuesLessThan0")}
              </div>
            )}
          </div>
        </>
      )}

      <div className="chart">
        <Charts
          plantId={plantId}
          cleaningResponse={cleaningResponse}
          date={date}
          cips={segmentedCips}
          threshold={threshold}
          activeStep={activeStep}
          iscleaningEffectivenesspage={true}
        />
      </div>
      <div className="descriptionText marginTop10 marginBtm10">
        {t("cleaningEffectivnessStrings.disclaimerText")}
      </div>
    </div>
  );
};

export default HistoricalCIPEffectivness;
